import { useState } from 'react';
import type { ProvisionCompleteEvent } from '../lib/platformApi/provisionStream';

interface CredentialsRevealScreenProps {
  result: ProvisionCompleteEvent;
  onDone: () => void;
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div style={{ marginBottom: 12 }}>
      <label className="lbl">{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input readOnly value={value} style={{ flex: 1, fontFamily: 'var(--mono)' }} />
        <button onClick={copy} style={{ background: 'var(--bg)', border: '1px solid #E5E7EB', borderRadius: 8, padding: '0 14px', fontSize: 13, cursor: 'pointer', minWidth: 68 }}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

// No way to re-fetch this password later, by design — matches the CLI's
// existing documented behavior ("generated — shown once, not recoverable").
// If it's lost, recovery is the tenant-side admin password reset
// (PATCH /api/users/:id, Admin-only), not anything in this console.
export default function CredentialsRevealScreen({ result, onDone }: CredentialsRevealScreenProps) {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <div style={{ maxWidth: 480, margin: '60px auto', padding: '0 20px' }}>
      <div className="card" style={{ padding: 28 }}>
        <div style={{ fontSize: 32, textAlign: 'center', marginBottom: 8 }}>✅</div>
        <div style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 4 }}>Tenant '{result.slug}' is active</div>
        <div style={{ fontSize: 12, color: '#6B7280', textAlign: 'center', marginBottom: 20 }}>
          {result.tables} tables · {result.admins} admin user{result.admins === 1 ? '' : 's'}
        </div>

        {result.adminUser && result.adminPassword ? (
          <>
            <div style={{ padding: '8px 12px', background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8, color: '#92400E', fontSize: 12, marginBottom: 16 }}>
              This password will not be shown again. Copy it now and relay it to the hospital's admin contact — the console
              has no way to recover it later; a lost password is reset from inside the tenant's own Admin panel.
            </div>
            <CopyField label="Admin username" value={result.adminUser} />
            <CopyField label="Admin password" value={result.adminPassword} />
          </>
        ) : (
          <div style={{ fontSize: 13, color: '#6B7280', textAlign: 'center', marginBottom: 16 }}>Seeding was skipped for this tenant — no admin credentials were created.</div>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '16px 0' }}>
          <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} />
          I've saved this password
        </label>

        <button className="btn-primary" style={{ width: '100%' }} disabled={!acknowledged} onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}
